'use client';

import { format, isToday, isTomorrow } from 'date-fns';
import { de, enUS, pt } from 'date-fns/locale';
import { Calendar, CheckCircle2, Clock, Package, XCircle } from 'lucide-react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { JSX } from 'react';

import { MealTextProvider } from '@/components/meal/MealTextProvider';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MealRatingPrompt } from '@/features/meal-rating/components/meal-rating-prompt';
import { MealRatingSubmitted } from '@/features/meal-rating/components/meal-rating-submitted';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/utils/date-only';

import type { EmployeeOrder, OrderStatus } from '../types';

interface OrderCardProps {
  order: EmployeeOrder;
  blockNavigation?: boolean;
}

function getStatusConfig(
  status: OrderStatus,
  t: ReturnType<typeof useTranslations<'employeeOrders'>>,
): {
  icon: JSX.Element;
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  iconContainerClassName: string;
  badgeClassName: string;
} {
  switch (status) {
    case 'delivered':
      return {
        icon: <CheckCircle2 className="h-[18px] w-[18px]" />,
        label: t('status.delivered'),
        variant: 'default',
        iconContainerClassName: 'bg-green-500/10 text-green-600',
        badgeClassName: 'bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20',
      };
    case 'cancelled':
      return {
        icon: <XCircle className="h-[18px] w-[18px]" />,
        label: t('status.cancelled'),
        variant: 'destructive',
        iconContainerClassName: 'bg-destructive/10 text-destructive',
        badgeClassName:
          'bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/20',
      };
    case 'confirmed':
      return {
        icon: <Package className="h-[18px] w-[18px]" />,
        label: t('status.confirmed'),
        variant: 'secondary',
        iconContainerClassName: 'bg-primary/10 text-primary',
        badgeClassName: 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20',
      };
    default:
      return {
        icon: <Clock className="h-[18px] w-[18px]" />,
        label: t('status.pending'),
        variant: 'outline',
        iconContainerClassName: 'bg-muted/50 text-muted-foreground',
        badgeClassName: '',
      };
  }
}

const DATE_LOCALES = {
  de,
  en: enUS,
  pt,
} as const;

function formatMealDate(
  dateStr: string,
  t: ReturnType<typeof useTranslations<'employeeOrders'>>,
  locale: string,
) {
  const date = parseDateOnly(dateStr);
  if (isToday(date)) {
    return t('date.today');
  }
  if (isTomorrow(date)) {
    return t('date.tomorrow');
  }
  const dateLocale = DATE_LOCALES[locale as keyof typeof DATE_LOCALES] ?? enUS;
  return format(date, 'EEE, d MMM', { locale: dateLocale });
}

export function OrderCard({ order, blockNavigation = false }: OrderCardProps): JSX.Element {
  const t = useTranslations('employeeOrders');
  const locale = useLocale();
  const statusConfig = getStatusConfig(order.status, t);
  const ratingTargetMealId =
    order.delivered_at && order.rating === null ? order.ratingTargetMealId : null;
  const orderDateHref = `/app/orders/${order.meal_date}`;

  const orderSummary = (
    <>
      <CardHeader className="pb-2 max-xs:p-4 max-xs:pb-2">
        <div className="flex min-h-[44px] items-center justify-between max-xs:flex-col max-xs:items-start max-xs:gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {/* Icon with subtle background - profile page pattern */}
            <span
              className={cn(
                'flex items-center justify-center',
                'h-9 w-9 shrink-0 rounded-xl',
                'bg-muted/50',
                'transition-colors duration-200',
              )}
            >
              <Calendar className="h-[18px] w-[18px] text-muted-foreground" />
            </span>
            <CardTitle className="truncate text-base font-medium max-xs:text-sm">
              {formatMealDate(order.meal_date, t, locale)}
            </CardTitle>
          </div>
          <Badge
            variant="outline"
            className={cn(
              'gap-1.5 transition-colors duration-200 max-xs:text-[10px]',
              statusConfig.badgeClassName,
            )}
          >
            {statusConfig.icon}
            <span>{statusConfig.label}</span>
          </Badge>
        </div>
        <CardDescription className="flex items-center gap-2 pl-12 max-xs:flex-wrap max-xs:pl-0 max-xs:text-xs">
          <Clock className="h-3.5 w-3.5" />
          {order.daypart === 'lunch' ? t('daypart.lunch') : t('daypart.dinner')}
          {order.canteen_name && (
            <>
              <span className="text-muted-foreground">•</span>
              <span>{order.canteen_name}</span>
            </>
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-2 max-xs:p-4 max-xs:pt-2">
        <div className="space-y-1">
          {order.meals.map((meal) => (
            <MealTextProvider
              key={meal.meal_id}
              mealId={meal.meal_id}
              fallbackName={meal.meal_name}
              fallbackDescription={meal.meal_description}
            >
              {({ name, description }) => (
                <div
                  className={cn(
                    'group/meal flex items-start justify-between',
                    'min-h-[44px] py-2.5 px-3 -mx-3',
                    'rounded-lg',
                    'hover:bg-accent/50',
                    'transition-all duration-200 ease-out',
                    'border-b last:border-0 border-border/50',
                    'max-xs:flex-col max-xs:items-start max-xs:gap-2 max-xs:py-2',
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate group-hover/meal:text-foreground transition-colors">
                        {name}
                      </span>
                      {meal.quantity > 1 && (
                        <Badge variant="outline" className="text-xs shrink-0">
                          x{meal.quantity}
                        </Badge>
                      )}
                    </div>
                    {description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {description}
                      </p>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0 max-xs:self-start">
                    {t(`course.${meal.course}`)}
                  </Badge>
                </div>
              )}
            </MealTextProvider>
          ))}
        </div>

        {order.delivered_at && (
          <div
            className={cn(
              'mt-3 pt-3 border-t text-xs text-muted-foreground',
              'flex items-center gap-2',
            )}
          >
            <span
              className={cn(
                'flex items-center justify-center',
                'h-6 w-6 rounded-lg',
                'bg-green-500/10',
              )}
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            </span>
            {t('deliveredAt', { time: format(new Date(order.delivered_at), 'HH:mm') })}
          </div>
        )}
      </CardContent>
    </>
  );

  return (
    <Card
      className={cn(
        'overflow-hidden',
        'hover:bg-accent/30',
        'active:scale-[0.99]',
        'transition-all duration-200 ease-out',
      )}
    >
      {blockNavigation ? orderSummary : <Link href={orderDateHref}>{orderSummary}</Link>}
      {(ratingTargetMealId || (order.delivered_at && order.rating !== null)) && (
        <CardContent className="pt-0 max-xs:p-4 max-xs:pt-0">
          {ratingTargetMealId && (
            <div className="mt-3 border-t pt-3">
              <MealRatingPrompt mealId={ratingTargetMealId} orderSlotId={order.order_slot_id} />
            </div>
          )}

          {order.delivered_at && order.rating !== null && (
            <div className="mt-3 border-t pt-3">
              <MealRatingSubmitted rating={order.rating} />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
